<template>
    <div class="chat-input-container">
        <label :for="inputId" class="sr-only">Chat message input</label>
        <div class="input-wrapper">
            <textarea
                :id="inputId"
                ref="textareaRef"
                v-model="inputText"
                :placeholder="placeholder"
                :maxlength="maxLength"
                :disabled="disabled"
                :aria-label="placeholder"
                @keydown.enter.exact.prevent="handleSend"
                @keydown.enter.shift.exact="handleNewline"
                class="chat-textarea"
                rows="1"
            ></textarea>
            <div class="input-actions">
                <span class="char-count" :class="{ 'char-warning': inputText.length > maxLength * 0.9 }">
                    {{ inputText.length }}/{{ maxLength }}
                </span>
                <button
                    class="send-button"
                    :class="{ 'send-button-active': canSend }"
                    :disabled="!canSend"
                    @click="handleSend"
                    aria-label="Send message"
                >
                    <span class="material-symbols-outlined">send</span>
                </button>
            </div>
        </div>
        <div class="input-hint">
            Press Enter to send, Shift+Enter for new line
        </div>
    </div>
</template>

<script>
/**
 * ChatInput Component
 * Text input field for chat messages with character count and keyboard shortcuts.
 */
export default {
    name: 'ChatInput',
    props: {
        /** @type {string} v-model binding for the input text */
        modelValue: {
            type: String,
            default: '',
        },
        /** @type {string} Placeholder text when empty */
        placeholder: {
            type: String,
            default: 'Ask about billings, events, or clients...',
        },
        /** @type {number} Maximum allowed character length */
        maxLength: {
            type: Number,
            default: 1000,
        },
        /** @type {boolean} Whether the input is disabled */
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:modelValue', 'send'],
    data() {
        return {
            inputText: this.modelValue,
        }
    },
    computed: {
        /** @returns {string} Unique ID for accessibility */
        inputId() {
            return `chat-input-${this._.uid}`
        },
        /** @returns {boolean} Whether the send button should be active */
        canSend() {
            return this.inputText.trim().length > 0 && !this.disabled
        },
    },
    watch: {
        modelValue(val) {
            this.inputText = val
        },
        inputText(val) {
            this.$emit('update:modelValue', val)
            this.adjustTextareaHeight()
        },
    },
    methods: {
        /** Emits send event with trimmed content */
        handleSend() {
            if (!this.canSend) return
            this.$emit('send', this.inputText.trim())
            this.inputText = ''
            this.$nextTick(() => this.adjustTextareaHeight())
        },
        /** Inserts newline character at cursor position */
        handleNewline() {
            const textarea = this.$refs.textareaRef
            const start = textarea.selectionStart
            const end = textarea.selectionEnd
            this.inputText = this.inputText.substring(0, start) + '\n' + this.inputText.substring(end)
            this.$nextTick(() => {
                textarea.selectionStart = textarea.selectionEnd = start + 1
            })
        },
        /** Auto-resizes textarea based on content */
        adjustTextareaHeight() {
            const textarea = this.$refs.textareaRef
            if (!textarea) return
            textarea.style.height = 'auto'
            textarea.style.height = Math.min(textarea.scrollHeight, 150) + 'px'
        },
        /** Focuses the textarea element */
        focus() {
            this.$refs.textareaRef?.focus()
        },
    },
    mounted() {
        this.adjustTextareaHeight()
    },
}
</script>

<style scoped>
.chat-input-container {
    padding: 16px;
    background: var(--color-surface);
    border-top: 1px solid var(--color-outline-variant);
}

.input-wrapper {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    background: var(--color-surface-container-low);
    border-radius: 24px;
    padding: 8px 8px 8px 16px;
    border: 1px solid var(--color-outline-variant);
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.input-wrapper:focus-within {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(119, 89, 40, 0.1);
}

.chat-textarea {
    flex: 1;
    border: none;
    background: transparent;
    resize: none;
    font-family: 'Manrope', sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: var(--color-on-surface);
    outline: none;
    padding: 8px 0;
    min-height: 24px;
    max-height: 150px;
}

.chat-textarea::placeholder {
    color: var(--color-outline);
}

.chat-textarea:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.input-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.char-count {
    font-size: 11px;
    color: var(--color-outline);
    font-family: 'Manrope', sans-serif;
}

.char-warning {
    color: var(--color-error);
}

.send-button {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: none;
    background: var(--color-surface-container-high);
    color: var(--color-outline);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: not-allowed;
    transition: all 0.2s ease;
}

.send-button-active {
    background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-container) 100%);
    color: var(--color-on-primary);
    cursor: pointer;
}

.send-button-active:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(119, 89, 40, 0.3);
}

.send-button-active:active {
    transform: scale(0.95);
}

.send-button .material-symbols-outlined {
    font-size: 20px;
}

.input-hint {
    margin-top: 8px;
    text-align: center;
    font-size: 11px;
    color: var(--color-outline);
}
</style>
